<template>
  <div class="c-config-content">
    <h4 class="c-config-content__title">{{ $t('sipExtension') }}</h4>
    <div>
      <div class="c-template-table table-responsive c-role-table">
        <table class="table table-hover">
          <thead>
          <tr style="background-color: #F1F5FB">
            <th>{{ $t('name') }}</th>
            <th>{{ $t('ext') }}</th>
            <th>{{ $t('group') }}</th>
            <th>{{ $t('status') }}</th>
            <th>{{ $t('sync') }}</th>
            <th class="th-80 text-right">
              <span @click="create" class="btn-add"><i class="icon16-plus-circle-gadient"></i></span>
            </th>
          </tr>
          </thead>
          <tbody v-if="items.length">
          <tr v-for="item in items" :key="item._id">
            <td>{{ item.name }}</td>
            <td>{{ item.ext }}</td>
            <td>{{ item.group ? item.group.name : '-' }}</td>
            <td>
              <span v-if="item.active" class="badge badge-success">{{ $t('active') }}</span>
              <span v-else class="badge badge-danger">{{ $t('inactive') }}</span>
            </td>
            <td>
              <span v-if="item.syncStatus === 'synced'" class="badge badge-success">Synced</span>
              <span v-else-if="item.syncStatus === 'pending'" class="badge badge-warning">Pending</span>
              <span v-else-if="item.syncStatus === 'failed'" class="badge badge-danger" v-b-tooltip.hover :title="item.syncError">Failed</span>
            </td>
            <td class="text-right">
              <button @click="update(item._id)" class="btn btn-outline-gray btn-sm" type="button">
                <font-awesome-icon icon="info"/>
              </button>
              <button v-if="item.active" @click="deactivate(item)" class="btn btn-outline-danger btn-sm" type="button" v-b-tooltip.hover :title="$t('deactivate')">
                <font-awesome-icon icon="ban"/>
              </button>
              <button v-else @click="activate(item)" class="btn btn-outline-success btn-sm" type="button" v-b-tooltip.hover :title="$t('activate')">
                <font-awesome-icon icon="check"/>
              </button>
              <button @click="resetPassword(item)" class="btn btn-outline-warning btn-sm" type="button" v-b-tooltip.hover :title="$t('resetPassword')">
                <font-awesome-icon icon="key"/>
              </button>
              <button v-if="item.syncStatus === 'failed'" @click="retrySync(item)" class="btn btn-outline-info btn-sm" type="button" v-b-tooltip.hover :title="$t('retrySync')">
                <font-awesome-icon icon="sync"/>
              </button>
            </td>
          </tr>
          </tbody>
          <tbody v-else>
          <tr>
            <td colspan="6" class="text-center text-gray-999">{{ $t('empty') }}</td>
          </tr>
          </tbody>
        </table>
      </div>
      <div class="c-paging" v-if="itemCount > filterFormModel.pageSize">
        <b-pagination size="sm" class="pagination pagination-sm justify-content-center"
                      v-model="filterFormModel.page" :total-rows="itemCount"
                      :per-page="filterFormModel.pageSize" @input="load">
        </b-pagination>
      </div>
      <b-modal v-model="showForm" :title="$t('formTitle')" @ok="save" no-close-on-backdrop>
        <form-input :label="$t('name')" :model="formModel" attribute="name" :errors="formErrors" />
        <form-input :label="$t('ext')" :model="formModel" attribute="ext" :errors="formErrors" :disabled="!!formModel._id" />
        <form-input :label="$t('extPassword')" :model="formModel" attribute="extPassword" :errors="formErrors" />
        <client-user-group-select :label="$t('group')" :model="formModel" attribute="group" :errors="formErrors" />
        <form-input :label="$t('ccuNumberLimit')" :model="formModel" attribute="ccuNumberLimit" :errors="formErrors" type="number" />
        <form-input :label="$t('desc')" :model="formModel" attribute="desc" :errors="formErrors" />
        <template v-slot:modal-footer>
          <b-button variant="secondary" class="float-right" @click="showForm = false">{{ $t('close') }}</b-button>
        </template>
      </b-modal>
    </div>
  </div>
</template>

<script>
import list from '@/mixins/List';
import crud from '@/mixins/CRUD';
import ClientUserGroupSelect from '@/components/ClientUserGroupSelect';

export default {
  props: ['client'],
  mixins: [list, crud],
  components: { ClientUserGroupSelect },
  data: function () {
    return {
      service: '/client-sip-extension',
      autoLoad: true,
      defaultFilterFormModel: {
        client: this.client._id
      },
      defaultFormModel: {
        client: this.client._id,
        name: '',
        ext: '',
        extPassword: '',
        group: null,
        ccuNumberLimit: 1,
        desc: '',
      },
    }
  },
  methods: {
    async deactivate(item) {
      try {
        await this.$service.put(`${this.service}/${item._id}/deactivate`);
        this.load();
      } catch (e) {
        if (e.response && e.response.data) {
          this.$swal({ text: e.response.data.message || 'Error', icon: 'error' });
        }
      }
    },
    async activate(item) {
      try {
        await this.$service.put(`${this.service}/${item._id}/activate`);
        this.load();
      } catch (e) {
        if (e.response && e.response.data) {
          this.$swal({ text: e.response.data.message || 'Error', icon: 'error' });
        }
      }
    },
    async resetPassword(item) {
      this.$swal({
        title: this.$t('resetPasswordConfirm'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('confirm'),
        cancelButtonText: this.$t('cancel'),
      }).then(async (result) => {
        if (result.value) {
          try {
            let res = await this.$service.post(`${this.service}/${item._id}/reset-password`);
            this.$swal({ title: `${this.$t('newPassword')}: ${res.data.extPassword}`, icon: 'success' });
            this.load();
          } catch (e) {
            this.$swal({ text: 'Error', icon: 'error' });
          }
        }
      });
    },
    async retrySync(item) {
      try {
        await this.$service.post(`${this.service}/${item._id}/retry-sync`);
        this.$swal({ text: this.$t('retrySyncSent'), icon: 'success' });
        this.load();
      } catch (e) {
        this.$swal({ text: 'Error', icon: 'error' });
      }
    },
  },
  i18n: {
    messages: {
      vi: {
        sipExtension: 'Máy nhánh SIP',
        formTitle: 'Thông tin máy nhánh',
        name: 'Tên',
        ext: 'Số máy nhánh',
        extPassword: 'Mật khẩu',
        group: 'Nhóm',
        ccuNumberLimit: 'Giới hạn cuộc gọi đồng thời',
        desc: 'Mô tả',
        status: 'Trạng thái',
        sync: 'Đồng bộ',
        active: 'Hoạt động',
        inactive: 'Dừng',
        deactivate: 'Vô hiệu hoá',
        activate: 'Kích hoạt',
        resetPassword: 'Đặt lại mật khẩu',
        resetPasswordConfirm: 'Đặt lại mật khẩu cho máy nhánh này?',
        newPassword: 'Mật khẩu mới',
        retrySync: 'Thử đồng bộ lại',
        retrySyncSent: 'Đã gửi yêu cầu đồng bộ lại',
        empty: 'Chưa có máy nhánh nào',
        close: 'Đóng',
        confirm: 'Xác nhận',
        cancel: 'Huỷ',
      },
      en: {
        sipExtension: 'SIP Extensions',
        formTitle: 'SIP Extension Info',
        name: 'Name',
        ext: 'Extension Number',
        extPassword: 'Password',
        group: 'Group',
        ccuNumberLimit: 'Max Concurrent Calls',
        desc: 'Description',
        status: 'Status',
        sync: 'Sync',
        active: 'Active',
        inactive: 'Inactive',
        deactivate: 'Deactivate',
        activate: 'Activate',
        resetPassword: 'Reset Password',
        resetPasswordConfirm: 'Reset password for this extension?',
        newPassword: 'New Password',
        retrySync: 'Retry Sync',
        retrySyncSent: 'Retry sync request sent',
        empty: 'No SIP extensions found',
        close: 'Close',
        confirm: 'Confirm',
        cancel: 'Cancel',
      }
    }
  }
}
</script>
